import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { photographerAPI } from '@services/photographerApi';
import { ROUTES } from '@utils/constants';

/**
 * Returns a callback that sends the photographer back to the dashboard
 * onboarding wizard after they finish an onboarding step.
 *
 * On mount it records whether onboarding was still incomplete, so a successful
 * save only redirects users who arrived via the wizard — not those editing
 * settings from the regular dashboard nav after setup is complete.
 *
 * @returns A function that navigates to the dashboard when the page was opened
 *   mid-onboarding. It returns `true` when it redirected so callers can fall
 *   back to their own navigation (e.g. the event detail page) when it did not.
 */
export function useOnboardingReturn(): () => boolean {
  const navigate = useNavigate();
  const cameFromOnboarding = useRef(false);

  useEffect(() => {
    let active = true;
    photographerAPI
      .getOnboardingStatus()
      .then(res => {
        if (active) {
          cameFromOnboarding.current = res.data.completed < res.data.total;
        }
      })
      .catch(() => {
        /* default to no redirect if onboarding status can't be loaded */
      });
    return () => {
      active = false;
    };
  }, []);

  return () => {
    if (cameFromOnboarding.current) {
      navigate(ROUTES.DASHBOARD);
      return true;
    }
    return false;
  };
}
